import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ContactForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      alert(t('contactForm.requiredAlert'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://formsubmit.co/ajax/5afa15f2e040d8062998c8dcedf16908', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send contact form');
      }

      setIsSent(true);
    } catch (error) {
      alert(t('contactForm.sendError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSent) {
    return (
      <motion.div
        className="py-24 bg-[#2A2A2A] text-center text-[#F5F5F5]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <h2 className="text-4xl font-semibold mb-4">{t('contactForm.successTitle')}</h2>
        <p className="text-lg text-[#CCCCCC]">{t('contactForm.successBody')}</p>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="space-y-6 bg-[#2A2A2A] p-10 rounded-lg shadow-lg"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1 }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block mb-2 text-sm font-medium text-[#F5F5F5]">
            {t('contactForm.name')}
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full p-3 bg-[#1F1F1F] text-[#F5F5F5] border border-[#444] rounded-md focus:ring-1 focus:ring-[#C8102E] outline-none"
          />
        </div>
        <div>
          <label htmlFor="email" className="block mb-2 text-sm font-medium text-[#F5F5F5]">
            {t('contactForm.email')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full p-3 bg-[#1F1F1F] text-[#F5F5F5] border border-[#444] rounded-md focus:ring-1 focus:ring-[#C8102E] outline-none"
          />
        </div>
      </div>

      <div>
        <label htmlFor="subject" className="block mb-2 text-sm font-medium text-[#F5F5F5]">
          {t('contactForm.subject')}
        </label>
        <select
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          required
          className="w-full p-3 bg-[#1F1F1F] text-[#F5F5F5] border border-[#444] rounded-md focus:ring-1 focus:ring-[#C8102E] outline-none"
        >
          <option value="">{t('contactForm.selectSubject')}</option>
          <option value="Project Inquiry">{t('contactForm.subjects.projectInquiry')}</option>
          <option value="Collaboration">{t('contactForm.subjects.collaboration')}</option>
          <option value="General Question">{t('contactForm.subjects.generalQuestion')}</option>
          <option value="Other">{t('contactForm.subjects.other')}</option>
        </select>
      </div>

      <div>
        <label htmlFor="message" className="block mb-2 text-sm font-medium text-[#F5F5F5]">
          {t('contactForm.message')}
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          className="w-full p-3 bg-[#1F1F1F] text-[#F5F5F5] border border-[#444] rounded-md focus:ring-1 focus:ring-[#C8102E] outline-none"
        ></textarea>
      </div>

      <div className="flex justify-center">
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-10 py-4 bg-[#5F5F5F] text-white rounded-md font-medium hover:bg-[#C8102E] transition-all duration-300"
        >
          {isSubmitting ? t('contactForm.sending') : t('contactForm.send')}
        </button>
      </div>
    </motion.form>
  );
};

export default ContactForm;
